import React, { useEffect, useRef, useState } from 'react';
import { RebookStatus } from '../../types';
import { OverlaySpinner } from '../../components/OverlaySpinner/OverlaySpinner';
import * as api from '../../services/api';
import styles from './CourierReviews.module.scss';
import { BRAND_COLORS } from '../../config';

import ReactStars from 'react-stars';

const ReactStarsComponent = ReactStars as React.ComponentType<any>;

type Props = {
    className?: string;
};

type SortOption = { value: string; label: string };

const SORT_OPTIONS: SortOption[] = [
    { value: 'top', label: 'Top Reviews' },
    { value: 'new', label: 'Newest' },
    { value: 'old', label: 'Oldest' },
];

export function CourierReviews(props: Props) {
    const [status, setStatus] = useState<RebookStatus>('idle');
    const [apiMessage, setApiMessage] = useState<string>('');

    const [sortby, setSortby] = useState<string>('top');
    const [lastPage, setLastPage] = useState<number>(1);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [reviewLists, setReviewList] = useState<any[]>([]);

    const [filterOpen, setFilterOpen] = useState<boolean>(false);
    const filterRef = useRef<HTMLDivElement>(null);

    // Close the sort dropdown on outside click. Listening on the node's root
    // (ShadowRoot when embedded, else document) is required: inside a shadow
    // root, events on document are retargeted to the host and composedPath()
    // is needed to see the real click target.
    useEffect(() => {
        if (!filterOpen) return;

        const onDocPointerDown = (event: Event) => {
            const path = (event as any).composedPath ? (event as any).composedPath() : [];
            if (filterRef.current && !path.includes(filterRef.current)) {
                setFilterOpen(false);
            }
        };

        const root: Node = filterRef.current?.getRootNode() ?? document;
        root.addEventListener('pointerdown', onDocPointerDown, true);
        return () => root.removeEventListener('pointerdown', onDocPointerDown, true);
    }, [filterOpen]);

    // pageLoading drives the inline pager spinner instead of the full-section
    // overlay. loadingTarget marks which control was clicked ('prev'/'next'/page).
    const [pageLoading, setPageLoading] = useState<boolean>(false);
    const [loadingTarget, setLoadingTarget] = useState<'prev' | 'next' | number | null>(null);

    async function loadReviews(page: number, sort: string, isInitial = false) {
        if (isInitial) {
            setStatus('in-progress');
        } else {
            setPageLoading(true);
        }
        try {
            const response = await api.get<any>(`/couriers/reviews?page=${page}&sortby=${sort}`);
            if (response.status) {
                setStatus('found');
                setLastPage(response.data.last_page);
                setCurrentPage(response.data.current_page);
                setReviewList(response.data.data);
            } else {
                setStatus('not-found');
                setApiMessage(response.message);
            }
        } catch (e) {
            setStatus('idle');
            setApiMessage('Something went wrong!');
        } finally {
            setPageLoading(false);
            setLoadingTarget(null);
        }
    }

    useEffect(() => {
        loadReviews(1, sortby, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function switchPage(page: number, sort: string, target: 'prev' | 'next' | number) {
        if (page < 1 || page > lastPage || pageLoading) {
            return;
        }
        setLoadingTarget(target);
        setSortby(sort);
        loadReviews(page, sort);
    }

    function handleSortSelect(value: string) {
        setFilterOpen(false);
        if (value !== sortby && !pageLoading) {
            setSortby(value);
            loadReviews(1, value);
        }
    }

    const activeLabel = SORT_OPTIONS.find((o) => o.value === sortby)?.label ?? 'Top Reviews';

    // Show at most 5 page numbers, centered on the current page.
    const MAX_PAGE_BUTTONS = 5;
    let startPage = Math.max(1, currentPage - Math.floor(MAX_PAGE_BUTTONS / 2));
    let endPage = Math.min(lastPage, startPage + MAX_PAGE_BUTTONS - 1);
    startPage = Math.max(1, endPage - MAX_PAGE_BUTTONS + 1);

    const pageNumbers: number[] = [];
    for (let p = startPage; p <= endPage; p++) {
        pageNumbers.push(p);
    }

    return (
        <div className={styles.root}>
            <OverlaySpinner enabled={status === 'in-progress'} />

            <div className={styles.header}>
                <h2 className={styles.title}>Courier Reviews</h2>

                <div className={styles.filter} ref={filterRef}>
                    <button
                        type="button"
                        className={styles.filterButton}
                        aria-haspopup="listbox"
                        aria-expanded={filterOpen}
                        disabled={pageLoading}
                        onClick={() => setFilterOpen((open) => !open)}
                    >
                        <span>{activeLabel}</span>
                        {pageLoading ? (
                            <span className={styles.filterSpinner} aria-hidden="true" />
                        ) : (
                            <svg
                                className={styles.filterChevron}
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        )}
                    </button>

                    {filterOpen && (
                        <ul className={styles.filterMenu} role="listbox">
                            {SORT_OPTIONS.map((option) => (
                                <li
                                    key={option.value}
                                    role="option"
                                    aria-selected={option.value === sortby}
                                    className={option.value === sortby ? styles.filterOptionActive : styles.filterOption}
                                    onClick={() => handleSortSelect(option.value)}
                                >
                                    {option.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {apiMessage && status !== 'found' && (
                <div className={styles.emptyState}>{apiMessage}</div>
            )}

            <div className={styles.reviewList}>
                {reviewLists.map((review: any, index: number) => (
                    <article className={styles.reviewCard} key={index}>
                        <div className={styles.avatar} aria-hidden="true">
                            {(review.user_name ?? '?').charAt(0).toUpperCase()}
                        </div>

                        <div className={styles.reviewBody}>
                            <div className={styles.metaRow}>
                                <span className={styles.userName}>{review.user_name}</span>
                                <span className={styles.dot}>&bull;</span>
                                <ReactStarsComponent
                                    className={styles.stars}
                                    count={5}
                                    value={Number(review.rating)}
                                    size={18}
                                    half={true}
                                    edit={false}
                                    color2={BRAND_COLORS.primary}
                                />
                                <span className={styles.dot}>&bull;</span>
                                <span className={styles.date}>{review.elapsed_time}</span>
                            </div>

                            {review.review && <p className={styles.reviewText}>{review.review}</p>}
                        </div>

                        {review.courier_image && (
                            <div className={styles.courierLogo}>
                                <img alt="courier" src={review.courier_image} />
                            </div>
                        )}
                    </article>
                ))}
            </div>

            {lastPage > 1 && (
                <div className={styles.pagination}>
                    <button
                        type="button"
                        className={styles.pageButton}
                        disabled={currentPage <= 1 || pageLoading}
                        onClick={() => switchPage(currentPage - 1, sortby, 'prev')}
                    >
                        {loadingTarget === 'prev' ? <span className={styles.pageSpinner} /> : 'Prev'}
                    </button>

                    {pageNumbers.map((page) => (
                        <button
                            type="button"
                            key={page}
                            className={page === currentPage ? styles.pageNumberActive : styles.pageNumber}
                            disabled={pageLoading}
                            onClick={() => switchPage(page, sortby, page)}
                        >
                            {loadingTarget === page ? <span className={styles.pageSpinner} /> : page}
                        </button>
                    ))}

                    <button
                        type="button"
                        className={styles.pageButton}
                        disabled={currentPage >= lastPage || pageLoading}
                        onClick={() => switchPage(currentPage + 1, sortby, 'next')}
                    >
                        {loadingTarget === 'next' ? <span className={styles.pageSpinner} /> : 'Next'}
                    </button>
                </div>
            )}
        </div>
    );
}
